import type { ContributionEntry, EnhancementContributionResponse, ReportHorizon } from "@eu.mdotm.api/sphere";
import {
	type OrderBy,
	type TableWithGroupsProps,
	AutoSortTableWithGroups,
	Row,
	TableDataCell,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSort, builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { DropdownMenu, Select, Switch } from "@mdotm/shadcn/mdotm/components";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import GraphLegend from "$root/components/GraphLegend";
import { InfoDelta } from "$root/components/InfoDelta";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { asOfMissingDataWall } from "$root/pages/PortfolioDetails/as-of/missingDataWall";
import {
	PortfolioQueryWidgetBase,
	portfolioWidgetMissingDataReason,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { BarGraphPCSvg, type BarGraphPCSvgProps } from "$root/ui-lib/charts";
import {
	type ContextContent,
	DrillDownByValueMap,
	downloadContentDisposition,
	getGraphMarkers,
	objSumRounded,
	qualifier,
	roundCustomByStep,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { horizonMap } from "../const";
import { buildWidgetExportConfig } from "../widget-export";

const categoryOptions = [
	{
		label: "Macro Asset Class vs Micro Asset Class",
		selector: "assetClass",
		vsSelector: "microAssetClass",
	},
	{
		label: "Macro Asset Class vs Geography",
		selector: "assetClass",
		vsSelector: "geography",
	},
	{
		label: "Micro Asset Class vs Geography",
		selector: "microAssetClass",
		vsSelector: "geography",
	},
	{
		label: "Geography vs Macro Asset Class",
		selector: "geography",
		vsSelector: "assetClass",
	},
	{
		label: "Geography vs Micro Asset Class",
		selector: "geography",
		vsSelector: "microAssetClass",
	},
];
const defaultCategorySelection = {
	selector: categoryOptions[0].selector,
	vsSelector: categoryOptions[0].vsSelector,
};
const ExanteContributionVolatility = (props: ContextContent<typeof PortfolioContext>) => {
	const [horizon] = useState<ReportHorizon>(horizonMap.ONE_YEAR.value);
	const [category, setCategory] = useState(defaultCategorySelection);

	const { portfolio, selectedBenchmark, enhanced, asOf, asOfSnapshot } = props;
	const uuid = props.portfolio?.uuid;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier ?? "";

	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY.TITLE"),
			tooltip: t("PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY.TOOLTIP"),
		}),
		[t],
	);

	const queryRaw = useQuery({
		...portfolioRepository.exanteContributionVolatility.getQueryConfig(uuid, {
			enhanced,
			horizon,
			benchmarkId,
			asOf,
		}),
		enabled: isAsOfReadable(asOf, asOfSnapshot),
	});

	const query = useMappedQuery(
		(exanteContributionVolatilityResponse) => {
			if (!uuid || !benchmarkId || !exanteContributionVolatilityResponse) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			const exanteContributionVolatility = {
				benchmark: enhanced
					? (exanteContributionVolatilityResponse as EnhancementContributionResponse).benchmark
					: undefined,
				current: enhanced
					? (exanteContributionVolatilityResponse as EnhancementContributionResponse).current
					: exanteContributionVolatilityResponse.current,
				proposal: enhanced
					? (exanteContributionVolatilityResponse as EnhancementContributionResponse).proposal
					: undefined,
			};

			if (enhanced && !exanteContributionVolatility.current && !exanteContributionVolatility.proposal) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			if (!enhanced && !exanteContributionVolatility.current && !exanteContributionVolatility.benchmark) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			return {
				data: exanteContributionVolatility,
				widgetStatus: WidgetStatus.READY,
			};
		},
		[benchmarkId, enhanced, props.portfolio, uuid],
		queryRaw,
	);

	return (
		<PortfolioQueryWidgetBase query={query}>
			{(exanteContributionVolatility) => (
				<ExanteContributionVolatilityInner
					ctx={props}
					exanteContributionVolatility={exanteContributionVolatility}
					category={category}
					onChangeCategory={setCategory}
					horizon={horizon}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
};

/** @internal — exported for tests */
export const ExanteContributionVolatilityInner = ({
	ctx,
	exanteContributionVolatility,
	category,
	onChangeCategory,
	horizon,
}: {
	ctx: ContextContent<typeof PortfolioContext>;
	exanteContributionVolatility: {
		benchmark: ContributionEntry[] | undefined;
		current: ContributionEntry[] | undefined;
		proposal: ContributionEntry[] | undefined;
	};
	category: { selector: string; vsSelector: string };
	onChangeCategory(opt: { selector: string; vsSelector: string }): void;
	horizon: ReportHorizon;
}) => {
	const { enhanced } = ctx;
	const { formatNumber, formatDate } = useLocaleFormatters();
	const { t } = useTranslation();

	const exanteData = useMemo(() => {
		const aggregateData = {
			volatility: (exanteContributionVolatility.current ?? []).map((el) => ({
				assetClass: el.assetClass,
				geography: el.geography,
				microAssetClass: el.microAssetClass,
				originalVolatility: el,
				volatilityValue: el.value,
				id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
			})),
			benchmarkVolatility: (exanteContributionVolatility.benchmark ?? []).map((el) => ({
				assetClass: el.assetClass,
				geography: el.geography,
				microAssetClass: el.microAssetClass,
				originalBenchmarkVolatility: el,
				benchmarkVolatility: el.value,
				id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
			})),
			enhancedVolatility: (exanteContributionVolatility.proposal ?? []).map((el) => ({
				assetClass: el.assetClass,
				geography: el.geography,
				microAssetClass: el.microAssetClass,
				originalEnhancedVolatility: el,
				enhancedVolatility: el.value,
				id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
			})),
		};

		const decupledByID = [
			...new Set([
				...aggregateData.volatility.map((el) => el.id),
				...aggregateData.benchmarkVolatility.map((el) => el.id),
				...aggregateData.enhancedVolatility.map((el) => el.id),
			]),
		];

		const result = decupledByID.map((id) => {
			const currentVTK = aggregateData.volatility.find((el) => el.id === id);
			const currentBVTK = aggregateData.benchmarkVolatility.find((el) => el.id === id);
			const currentEVTK = aggregateData.enhancedVolatility.find((el) => el.id === id);
			return {
				...currentVTK,
				...currentBVTK,
				...currentEVTK,
			};
		});
		return result;
	}, [exanteContributionVolatility]);
	const [filterBySignificant, setFilterBySignificant] = useState(true);

	// Mirror the table's column sort so the export reproduces it. Defaults to none (the provider's natural
	// order); once the user sorts a column the export carries that same sort.
	const [orderBy, setOrderBy] = useState<OrderBy[]>([]);
	const computedContribution = useMemo(() => {
		const drilldown = DrillDownByValueMap(
			exanteData ?? [],
			category.selector as keyof (typeof exanteData)[number],
			category.vsSelector as keyof (typeof exanteData)[number],
			{
				volatilityValue: (val) => objSumRounded(val, "volatilityValue"),
				benchmarkVolatility: (val) => objSumRounded(val, "benchmarkVolatility"),
				enhancedVolatility: (val) => objSumRounded(val, "enhancedVolatility"),
				max: (val) =>
					Math.max(
						objSumRounded(val, "volatilityValue") ?? 0,
						objSumRounded(val, "benchmarkVolatility") ?? 0,
						objSumRounded(val, "enhancedVolatility") ?? 0,
					),
				min: (val) =>
					Math.min(
						objSumRounded(val, "volatilityValue") ?? 0,
						objSumRounded(val, "benchmarkVolatility") ?? 0,
						objSumRounded(val, "enhancedVolatility") ?? 0,
					),
			},
			builtInSortFnFor("volatilityValue", "desc"),
		);
		if (!filterBySignificant) {
			return drilldown;
		}
		return {
			...drilldown,
			drilldownData: drilldown.drilldownData
				.filter(
					(x) =>
						(x.computedData.volatilityValue != null && Math.abs(x.computedData.volatilityValue) >= 0.01) ||
						(enhanced &&
							x.computedData.enhancedVolatility != null &&
							Math.abs(x.computedData.enhancedVolatility) >= 0.01),
				)
				.map((x) => ({
					...x,
					drilldown: x.drilldown.filter(
						(y) =>
							(y.computedData.volatilityValue != null && Math.abs(y.computedData.volatilityValue) >= 0.01) ||
							(enhanced &&
								y.computedData.enhancedVolatility != null &&
								Math.abs(y.computedData.enhancedVolatility) >= 0.01),
					),
				})),
		};
	}, [exanteData, category.selector, category.vsSelector, filterBySignificant, enhanced]);

	// Correction Of Graph Scala
	const graphLimits = useMemo(() => {
		const value = computedContribution.drilldownData.reduce(
			(acc, group) => {
				const rowValues = group.drilldown.reduce(
					(a, row) => {
						return {
							maxVolatility: Math.max(a.maxVolatility, row.computedData.max, group.computedData.max),
							minVolatility: Math.min(a.minVolatility, row.computedData.min, group.computedData.min),
						};
					},
					{ maxVolatility: 0, minVolatility: 0 },
				);
				return {
					maxVolatility: Math.max(acc.maxVolatility, rowValues.maxVolatility),
					minVolatility: Math.min(acc.minVolatility, rowValues.minVolatility),
				};
			},
			{ maxVolatility: 0, minVolatility: 0 },
		);
		return {
			maxVolatility: roundCustomByStep(value.maxVolatility, 1),
			minVolatility: roundCustomByStep(value.minVolatility, 1),
		};
	}, [computedContribution.drilldownData]);

	// Generate Markers
	const graphColumnMarkers = useMemo(
		() => ({
			markersVolatility: getGraphMarkers(graphLimits.maxVolatility, graphLimits.minVolatility, 1, "%", 12),
		}),
		[graphLimits],
	);

	// Get constribution translation Object Options
	const constributionT = useMemo(
		() =>
			t(`PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY`, {
				returnObjects: true,
			}),
		[t],
	);

	const columns = useCallback<
		TableWithGroupsProps<
			(typeof computedContribution.drilldownData)[number] & {
				rows: (typeof computedContribution.drilldownData)[number]["drilldown"];
			}
		>["columns"]
	>(
		({ expandColumn }) =>
			enhanced
				? [
						expandColumn,
						{
							header: constributionT.TABLE[category.selector as keyof typeof constributionT.TABLE],
							groupContent: (groupedRow) => String(groupedRow.value as any),
							content: (row) => String(row.value as any),
							sortFn: builtInSortFnFor("value"),
							name: "value",
							minWidth: 200,
						},
						{
							name: "current",
							headerCellClassName: "whitespace-pre-line",
							header: `Composition\n${formatDate(ctx.portfolio.modificationDate, { omitYear: true })}`,
							groupContent: (groupedRow, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#005C8B] font-semibold tabular-nums">{`${formatNumber(
										groupedRow.computedData.volatilityValue,
										2,
									)}%`}</span>
								</TableDataCell>
							),
							content: (row, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#005C8B] tabular-nums">
										{`${formatNumber(row.computedData.volatilityValue, 2)}%`}
									</span>
								</TableDataCell>
							),
							width: 120,
							align: "end",
							sortFn(a, b) {
								return builtInSort(a.computedData.volatilityValue, b.computedData.volatilityValue);
							},
						},
						{
							name: "proposal",
							header: "PROPOSAL",
							groupContent: (groupedRow, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#00AEEF] font-semibold tabular-nums">
										{`${formatNumber(groupedRow.computedData.enhancedVolatility, 2)}%`}
									</span>
								</TableDataCell>
							),
							content: (row, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#00AEEF] tabular-nums">
										{`${formatNumber(row.computedData.enhancedVolatility, 2)}%`}
									</span>
								</TableDataCell>
							),
							width: 120,
							sortFn(a, b) {
								return builtInSort(a.computedData.enhancedVolatility, b.computedData.enhancedVolatility);
							},
							align: "end",
						},
						{
							header: "DELTA",
							width: 120,
							groupContent: ({ computedData: { volatilityValue, enhancedVolatility } }, cellProps) => (
								<Row {...cellProps} className="font-bold" justifyContent="stretch" alignItems="center">
									<InfoDelta diff={(enhancedVolatility ?? 0) - (volatilityValue ?? 0)} enh={enhancedVolatility ?? 0} />
								</Row>
							),
							content: ({ computedData: { volatilityValue, enhancedVolatility } }, cellProps) => (
								<Row {...cellProps} className="font-bold" justifyContent="stretch" alignItems="center">
									<InfoDelta diff={(enhancedVolatility ?? 0) - (volatilityValue ?? 0)} enh={enhancedVolatility ?? 0} />
								</Row>
							),
							sortFn: (a, b) => {
								const deltaA = (a.computedData.enhancedVolatility ?? 0) - (a.computedData.volatilityValue ?? 0);
								const deltaB = (b.computedData.enhancedVolatility ?? 0) - (b.computedData.volatilityValue ?? 0);
								return builtInSort(deltaA, deltaB);
							},
							name: "difference",
							align: "end",
						},
						{
							name: "graph",
							orderable: false,
							header: (headerProps) => (
								<Row {...headerProps} justifyContent="space-between" alignItems="center">
									<ForEach collection={graphColumnMarkers.markersVolatility}>
										{(m) => (
											<Text as="div" type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
												{m.label}
											</Text>
										)}
									</ForEach>
								</Row>
							),
							groupContent: (
								{ computedData: { volatilityValue, benchmarkVolatility, enhancedVolatility } },
								cellProps,
							) => {
								const validValues = [
									{ value: volatilityValue, color: "#005C8B" },
									{ value: enhancedVolatility, color: "#00AEEF" },
									{ value: benchmarkVolatility, color: "#C6ACD9" },
								].filter((x): x is BarGraphPCSvgProps["data"][number] => x.value != null);

								if (validValues.length === 0) {
									return null;
								}
								return (
									<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
										<BarGraphPCSvg
											className="w-full"
											options={{
												resize: true,
												marksLabels: false,
												markerStep: 1,
												scale: {
													max: graphLimits.maxVolatility,
													min: graphLimits.minVolatility,
												},
											}}
											data={validValues}
										/>
									</Row>
								);
							},
							content: ({ computedData: { volatilityValue, benchmarkVolatility, enhancedVolatility } }, cellProps) => {
								const validValues = [
									{ value: volatilityValue, color: "#005C8B" },
									{ value: enhancedVolatility, color: "#00AEEF" },
									{ value: benchmarkVolatility, color: "#C6ACD9" },
								].filter((x): x is BarGraphPCSvgProps["data"][number] => x.value != null);

								if (validValues.length === 0) {
									return null;
								}
								return (
									<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
										<BarGraphPCSvg
											className="w-full"
											options={{
												resize: true,
												marksLabels: false,
												markerStep: 1,
												scale: {
													max: graphLimits.maxVolatility,
													min: graphLimits.minVolatility,
												},
											}}
											data={validValues}
										/>
									</Row>
								);
							},
							minWidth: 300,
						},
					]
				: [
						expandColumn,
						{
							name: "current",
							header: constributionT.TABLE[category.selector as keyof typeof constributionT.TABLE],
							groupContent: (groupedRow) => String(groupedRow.value as any),
							content: (row) => row.value as any,
							minWidth: 300,
						},
						{
							name: "volatility",
							header: "PORTFOLIO VOLATILITY",
							groupContent: (groupedRow, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#572C7F] font-semibold tabular-nums">{`${formatNumber(
										groupedRow.computedData.volatilityValue,
										2,
									)}%`}</span>
								</TableDataCell>
							),
							content: (row, cellProps) => (
								<TableDataCell {...cellProps}>
									<span className="text-[#572C7F] tabular-nums">{`${formatNumber(
										row.computedData.volatilityValue,
										2,
									)}%`}</span>
								</TableDataCell>
							),
							width: 130,
							sortFn(a, b) {
								return builtInSort(a.computedData.volatilityValue, b.computedData.volatilityValue);
							},
							align: "end",
						},
						{
							name: "graph",
							orderable: false,
							header: (headerProps) => (
								<Row {...headerProps} justifyContent="space-between" alignItems="center">
									<ForEach collection={graphColumnMarkers.markersVolatility}>
										{(m) => (
											<Text as="div" type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
												{m.label}
											</Text>
										)}
									</ForEach>
								</Row>
							),
							groupContent: ({ computedData: { volatilityValue, benchmarkVolatility } }, cellProps) => {
								const validValues = [
									{ value: volatilityValue, color: "#572C7F" },
									{ value: benchmarkVolatility, color: "#C6ACD9" },
								].filter((x): x is BarGraphPCSvgProps["data"][number] => x.value != null);

								if (validValues.length === 0) {
									return null;
								}
								return (
									<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
										<BarGraphPCSvg
											className="w-full"
											options={{
												resize: true,
												marksLabels: false,
												markerStep: 1,
												scale: {
													max: graphLimits.maxVolatility,
													min: graphLimits.minVolatility,
												},
											}}
											data={validValues}
										/>
									</Row>
								);
							},
							content: ({ computedData: { volatilityValue, benchmarkVolatility } }, cellProps) => {
								const validValues = [
									{ value: volatilityValue, color: "#572C7F" },
									{ value: benchmarkVolatility, color: "#C6ACD9" },
								].filter((x): x is BarGraphPCSvgProps["data"][number] => x.value != null);

								if (validValues.length === 0) {
									return null;
								}
								return (
									<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
										<BarGraphPCSvg
											className="w-full"
											options={{
												resize: true,
												marksLabels: false,
												markerStep: 1,
												scale: {
													max: graphLimits.maxVolatility,
													min: graphLimits.minVolatility,
												},
											}}
											data={validValues}
										/>
									</Row>
								);
							},
							minWidth: 300,
						},
					],
		[
			category.selector,
			constributionT,
			ctx.portfolio.modificationDate,
			enhanced,
			formatDate,
			formatNumber,
			graphColumnMarkers.markersVolatility,
			graphLimits.maxVolatility,
			graphLimits.minVolatility,
		],
	);

	// Download — mirrors the visible table via the smart-workspace `investmentCompositionVolatilityContribution`
	// data provider. The selected category drives the server-side aggregation; horizon and the significant-only
	// toggle mirror the current widget state.
	const exportVolatilityContribution = async () => {
		const config = buildWidgetExportConfig(
			"ExanteContributionVolatility",
			{
				investmentUuid: ctx.portfolio.uuid,
				horizon,
				selector: category.selector,
				vsSelector: category.vsSelector,
				onlySignificant: filterBySignificant,
				source: enhanced ? "PROPOSAL" : "CURRENT",
				proposalDate: ctx.portfolio.modificationDate,
				sort: orderBy,
			},
			{ asOf: ctx.asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export ex-ante volatility contribution",
					payload: JSON.stringify(config),
				},
			},
		);
	};

	const exportVolatilityContributionRef = useUnsafeUpdatedRef(exportVolatilityContribution);

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY.TITLE"),
			tooltip: t("PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY.TOOLTIP"),
			actionHeader: computedContribution.drilldownData.length ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{
						"data-qualifier": qualifier.widgets.portfolioExAnteContributionVolatility.downloadButton,
					}}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportVolatilityContributionRef.current();
							},
							label: t("PORTFOLIO_EXANTE_CONTRIBUTION_VOLATILITY.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioExAnteContributionVolatility.downloadItem,
						},
					]}
				/>
			) : null,
		}),
		[computedContribution.drilldownData.length, exportVolatilityContributionRef, t],
	);

	return (
		<div className="flex flex-col min-h-0">
			<div className="flex justify-between">
				<div className="inline-block">
					<Select
						value={category}
						onChange={onChangeCategory}
						options={categoryOptions.map(({ label, selector, vsSelector }) => ({
							label,
							value: { selector, vsSelector },
						}))}
					/>
				</div>
				<div className="inline-block">
					<Switch onChange={setFilterBySignificant} checked={filterBySignificant}>
						Significant values only
					</Switch>
				</div>
			</div>
			{asOfMissingDataWall(ctx.asOf, computedContribution.drilldownData.length === 0) ?? (
				<div className="w-full pb-3 mt-3 mb-2 px-2 flex flex-col min-h-0">
					<AutoSortTableWithGroups
						groupedRows={computedContribution.drilldownData.map((el) => ({
							...el,
							rows: el.drilldown,
						}))}
						groupRowKey={(r) => r.index}
						columns={columns}
						orderBy={orderBy}
						onOrderByChange={setOrderBy}
					/>
				</div>
			)}
			{enhanced ? (
				<GraphLegend>
					<div className="legend-container light book" style={{ marginRight: "1rem" }}>
						<ColoredRectangle color="#005C8B" variant="vertical" />{" "}
						{`Composition ${formatDate(ctx.portfolio.modificationDate, { omitYear: true })}`}
					</div>
					<div className="legend-container light book">
						<ColoredRectangle color="#00AEEF" variant="vertical" /> Proposal
					</div>
				</GraphLegend>
			) : (
				<GraphLegend>
					<div className="legend-container light book" style={{ marginRight: "1rem" }}>
						<ColoredRectangle color="#572C7F" variant="vertical" /> Portfolio ex-ante
					</div>
				</GraphLegend>
			)}
		</div>
	);
};

export default withContext(PortfolioContext)(ExanteContributionVolatility);
